import { createDraftSafeSelector } from "@reduxjs/toolkit";
import { RootState } from "@store/store";
import { MessageWizardTargetType } from "./messageWizard.types";

export const currentQuestionSelector = createDraftSafeSelector(
  (state: RootState) => state.messageWizard.questions,
  (state: RootState) => state.messageWizard.savedResults,
  (state: RootState) => state.messageWizard.isCompleted,
  (questions, savedResults, isCompleted) => {
    if (isCompleted) {
      return null;
    }

    if (!savedResults.length) {
      return questions.find((question) => question.index === 0 && !question.overrides) || null;
    }

    const lastSavedResult = savedResults.find((savedResult) => {
      const { questionId, answerId } = savedResult;
      const relatedQuestion = questions.find((question) => question.wqId === questionId);
      const nextTarget = relatedQuestion?.options.find(
        (option) => option.optionId === answerId
      )?.target;
      return (
        nextTarget?.type === MessageWizardTargetType.Question &&
        savedResults.every((savedResult) => savedResult.questionId !== nextTarget?.targetId)
      );
    });

    if (lastSavedResult) {
      const relatedQuestion = questions.find(
        (question) => question.wqId === lastSavedResult.questionId
      );
      const relatedOption = relatedQuestion?.options.find(
        (option) => option.optionId === lastSavedResult.answerId
      );
      const nextTarget = relatedOption?.target;

      if (nextTarget && nextTarget.type === MessageWizardTargetType.Question) {
        return questions.find((question) => question.wqId === nextTarget.targetId) || null;
      }
    }

    return null;
  }
);

export const savedResultsSelector = createDraftSafeSelector(
  (state: RootState) => state.messageWizard.questions,
  (state: RootState) => state.messageWizard.savedResults,
  (questions, savedResults) => {
    return savedResults
      .map((savedResult) => {
        const question = questions.find((question) => question.wqId === savedResult.questionId);
        const answer = question?.options.find((option) => option.optionId === savedResult.answerId);

        if (!question?.showInHistory || !answer) {
          return null;
        }

        return {
          question,
          answer,
        };
      })
      .filter((savedResults) => savedResults !== null);
  }
);
